import {
    DWT_DEFRA_SUBMISSION_STATUS_CODES,
    DWT_DEFRA_SUBMISSIONS_END_DATE_ELEMENT_ID,
    DWT_DEFRA_SUBMISSIONS_FACILITY_CODE_ELEMENT_ID,
    DWT_DEFRA_SUBMISSIONS_RECORD_COUNT_ALLOWED_NUMBERS,
    DWT_DEFRA_SUBMISSIONS_RECORD_COUNT_ELEMENT_ID,
    DWT_DEFRA_SUBMISSIONS_START_DATE_ELEMENT_ID,
    DWT_DEFRA_SUBMISSIONS_STATUS_ELEMENT_ID,
} from '../dwt-defra-submissions.constants';
import type {
    DwtDefraSubmissionsPayload,
    DwtDefraSubmissionsTaskPaneFilter,
} from '../models/dwt-defra-submissions.model';

export type DwtDefraSubmissionsFilterValidation =
    | { ok: true }
    | { ok: false; message: string; invalidElementIds: readonly string[] };

const LOCAL_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

function isValidDateOnly(iso: string): boolean {
    const match = LOCAL_DATE_ONLY.exec(iso);
    if (!match) {
        return false;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

function utcDayIndex(iso: string): number {
    const match = LOCAL_DATE_ONLY.exec(iso);
    if (!match) {
        return NaN;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
}

/**
 * Client-side validation for the DEFRA submissions task-pane filter (before Get-RegulatoryReports).
 * Blank dates are valid: they resolve to the last 7 days, including today.
 */
export function validateDwtDefraSubmissionsFilter(
    filter: DwtDefraSubmissionsTaskPaneFilter,
    now: Date = new Date()
): DwtDefraSubmissionsFilterValidation {
    if (filter.facilityCode.length === 0) {
        return {
            ok: false,
            message: 'Select one or more facilities.',
            invalidElementIds: [DWT_DEFRA_SUBMISSIONS_FACILITY_CODE_ELEMENT_ID],
        };
    }

    if (filter.status.length === 0) {
        return {
            ok: false,
            message: 'Select at least one status (Successful, Warnings, or Errors).',
            invalidElementIds: [DWT_DEFRA_SUBMISSIONS_STATUS_ELEMENT_ID],
        };
    }

    const unknownStatus = filter.status.find(
        (status) => !(DWT_DEFRA_SUBMISSION_STATUS_CODES as readonly number[]).includes(status)
    );
    if (unknownStatus !== undefined) {
        return {
            ok: false,
            message: 'Status must be Successful, Warnings, or Errors.',
            invalidElementIds: [DWT_DEFRA_SUBMISSIONS_STATUS_ELEMENT_ID],
        };
    }

    if (
        filter.recordCount !== undefined &&
        !DWT_DEFRA_SUBMISSIONS_RECORD_COUNT_ALLOWED_NUMBERS.includes(filter.recordCount)
    ) {
        return {
            ok: false,
            message: 'Record count must be one of the allowed values.',
            invalidElementIds: [DWT_DEFRA_SUBMISSIONS_RECORD_COUNT_ELEMENT_ID],
        };
    }

    const startDate = filter.startDate?.trim() ?? '';
    const endDate = filter.endDate?.trim() ?? '';

    if (startDate && !isValidDateOnly(startDate)) {
        return {
            ok: false,
            message: 'Start date must be a valid date (dd/mm/yyyy).',
            invalidElementIds: [DWT_DEFRA_SUBMISSIONS_START_DATE_ELEMENT_ID],
        };
    }

    if (endDate && !isValidDateOnly(endDate)) {
        return {
            ok: false,
            message: 'End date must be a valid date (dd/mm/yyyy).',
            invalidElementIds: [DWT_DEFRA_SUBMISSIONS_END_DATE_ELEMENT_ID],
        };
    }

    if (startDate && endDate && utcDayIndex(endDate) < utcDayIndex(startDate)) {
        return {
            ok: false,
            message: 'Start date must be on or before end date.',
            invalidElementIds: [
                DWT_DEFRA_SUBMISSIONS_START_DATE_ELEMENT_ID,
                DWT_DEFRA_SUBMISSIONS_END_DATE_ELEMENT_ID,
            ],
        };
    }

    return { ok: true };
}

/**
 * Validates the Get-RegulatoryReports Activity Handler request before POST.
 */
export function validateDwtDefraSubmissionsFilterRequest(
    payload: DwtDefraSubmissionsPayload
): DwtDefraSubmissionsFilterValidation {
    const filter = payload.data?.filters;
    if (!filter) {
        return {
            ok: false,
            message: 'Select one or more facilities.',
            invalidElementIds: [DWT_DEFRA_SUBMISSIONS_FACILITY_CODE_ELEMENT_ID],
        };
    }

    return validateDwtDefraSubmissionsFilter({
        facilityCode: filter.facilityCode,
        status: filter.status,
        recordCount: filter.recordCount,
    });
}
